import type { ModuleKey } from '../session/store/sessionTypes';
import type { GlobalFeatureFlagsMap } from './domain/globalFeatureFlags';
import type { PlanEntitlementsRow } from './domain/planEntitlements';
import type { BusinessCapability, OrganizationBusinessProfile } from '../session/domain/businessProfile';
import type { NavItemId } from '../navigation/navItemId';

const MODULE_CAPABILITY: Partial<Record<ModuleKey, BusinessCapability>> = {
  POS: 'pos',
  Catalog: 'catalog',
  Inventory: 'inventory',
  Customers: 'customer_credit_fiado',
  Reports: 'reports',
  BranchSettings: 'employees',
  Promotions: 'promotions',
  PriceLists: 'price_lists',
  Loyalty: 'customer_loyalty',
  Expenses: 'expenses',
  Purchases: 'purchases',
  Agenda: 'appointments',
  WorkOrders: 'work_orders',
  Commissions: 'commissions',
  Cash: 'cash_sessions',
  Checks: 'checks',
  Tables: 'tables',
  Kitchen: 'kitchen_tickets',
  MenuOptions: 'menu_modifiers',
  Team: 'employees',
  Organization: 'employees',
  Billing: 'employees',
  Support: 'employees',
  Orders: 'delivery_fulfillment',
};

const NAV_CAPABILITY: Partial<Record<NavItemId, BusinessCapability>> = {
  pos: 'pos',
  salesList: 'sales_history',
  cash: 'cash_sessions',
  expenses: 'expenses',
  purchases: 'purchases',
  catalog: 'catalog',
  inventory: 'inventory',
  fiado: 'customer_credit_fiado',
  promotions: 'promotions',
  priceLists: 'price_lists',
  // #1453: Combos vive bajo Opciones de menú pero se apaga por rubro/owner.
  productCombos: 'product_combos',
  loyalty: 'customer_loyalty',
  reports: 'reports',
  profile: 'employees',
  boothRentals: 'commissions',
  diningTables: 'tables',
  diningReservations: 'tables',
  kitchenQueue: 'kitchen_tickets',
  menuOptionGroups: 'menu_modifiers',
  appointments: 'appointments',
  workOrders: 'work_orders',
  commissions: 'commissions',
  myCommissions: 'commissions',
  commissionPlans: 'commissions',
  settlementDetail: 'commissions',
  quotes: 'service_quotes',
  branchIdentity: 'employees',
  branchPosOps: 'employees',
  storefrontHub: 'employees',
  deliverySettings: 'delivery_fulfillment',
  storeReviews: 'employees',
  integrationsHub: 'employees',
};

export type CapabilityGateContext = {
  profile: OrganizationBusinessProfile | null;
  entitlements: PlanEntitlementsRow | null;
  featureFlags: GlobalFeatureFlagsMap | null;
};

export function hasCapability(
  profile: OrganizationBusinessProfile | null,
  capability: BusinessCapability,
): boolean {
  // Fail-closed: sin Session Context no hay rubro. El boot gate no monta UI operativa sin profile.
  if (!profile) return false;
  return profile.capabilities.includes(capability);
}

export function resolveEffectiveCapabilities(ctx: CapabilityGateContext): Set<BusinessCapability> {
  if (!ctx.profile) return new Set();

  const base = new Set<BusinessCapability>(ctx.profile.capabilities);
  if (base.size === 0) {
    // ponytail: pack vacío en profile válido (cache vieja) → retail hasta re-bootstrap
    [
      'pos',
      'cash_sessions',
      'catalog',
      'inventory',
      'sales_history',
      'reports',
      'expenses',
      'employees',
      'customer_credit_fiado',
      'barcode_scan',
      'misc_quick_sale',
      'promotions',
      'external_services',
      'delivery_fulfillment',
      'fiscal_electronic',
      'mp_qr',
      'mp_oauth',
    ].forEach((c) => base.add(c as BusinessCapability));
  }

  if (ctx.featureFlags?.fiado_integrations === false) base.delete('customer_credit_fiado');
  if (ctx.featureFlags?.promotions_integrations === false) base.delete('promotions');
  if (ctx.featureFlags?.price_lists_integrations === false) base.delete('price_lists');
  if (ctx.featureFlags?.loyalty_integrations === false) base.delete('customer_loyalty');
  if (ctx.featureFlags?.expenses_module === false) base.delete('expenses');
  if (ctx.featureFlags?.mercadopago_integrations === false) {
    base.delete('mp_qr');
    base.delete('mp_oauth');
  }
  if (ctx.featureFlags?.arca_integrations === false) base.delete('fiscal_electronic');

  // #710: kill-switch global para variantes — desactiva la capability sin importar el rubro.
  if (ctx.featureFlags?.product_variants === false) base.delete('product_variants');

  // Compras depende de inventory (recepción) + expenses (deuda/pago).
  // Si alguno falta por rubro, toggle o flag, Compras no tiene sentido.
  if (!base.has('inventory') || !base.has('expenses')) base.delete('purchases');

  // ponytail: cache de profile previa al seed delivery_fulfillment → retail sigue viendo Delivery
  if (!base.has('delivery_fulfillment') && base.has('inventory') && !base.has('service_catalog')) {
    base.add('delivery_fulfillment');
  }

  if (ctx.entitlements && !ctx.entitlements.fiado_manual_only && !ctx.entitlements.fiado_payment_link) {
    base.delete('customer_credit_fiado');
  }
  if (ctx.entitlements && !ctx.entitlements.promotions_enabled) base.delete('promotions');
  if (ctx.entitlements && !ctx.entitlements.fiscal_enabled) base.delete('fiscal_electronic');
  if (ctx.entitlements && !ctx.entitlements.mp_qr_pos) {
    base.delete('mp_qr');
    base.delete('mp_oauth');
  }

  return base;
}

export function moduleAllowedByCapabilities(
  module: ModuleKey,
  capabilities: Set<BusinessCapability>,
): boolean {
  const required = MODULE_CAPABILITY[module];
  if (!required) return true;
  return capabilities.has(required);
}

export function navItemAllowedByCapabilities(
  navId: NavItemId,
  capabilities: Set<BusinessCapability>,
): boolean {
  const required = NAV_CAPABILITY[navId];
  if (!required) return true;
  return capabilities.has(required);
}

export function allowsWeightSales(profile: OrganizationBusinessProfile | null): boolean {
  return hasCapability(profile, 'weight_uom');
}

export function allowsProductVariants(profile: OrganizationBusinessProfile | null): boolean {
  return hasCapability(profile, 'product_variants');
}

export function allowsExternalServices(profile: OrganizationBusinessProfile | null): boolean {
  return hasCapability(profile, 'external_services');
}

export function allowsHospitality(profile: OrganizationBusinessProfile | null): boolean {
  return hasCapability(profile, 'tables') || hasCapability(profile, 'kitchen_tickets');
}

/** Filtros carta (alérgenos / gustos) en Catálogo — grupo Comida vía señales gastro. */
export function allowsMenuDietaryFilters(profile: OrganizationBusinessProfile | null): boolean {
  return (
    hasCapability(profile, 'kitchen_tickets') ||
    hasCapability(profile, 'menu_modifiers') ||
    hasCapability(profile, 'tables')
  );
}
